/**
 * RefineVoiceWidget - Self-contained voice + text editor widget
 *
 * This is the main public API for embedding RefineVoice editor instances.
 * Each widget has isolated state, supporting multiple instances on the same page.
 *
 * Features:
 * - Hybrid voice + keyboard input
 * - Real-time transcription with Deepgram
 * - Word-level alternatives and confidence scores
 * - Accessibility-first design
 * - Multi-instance support via isolated stores
 * - Optional text refinement functionality
 *
 * Example usage:
 * ```tsx
 * <RefineVoiceWidget
 *   widgetId="my-editor"
 *   onTranscriptChange={(text) => console.log(text)}
 *   placeholder="Start speaking or typing..."
 *   showRefineButton={true}
 *   refinePosition="bottom-right"
 *   onRefine={(original, refined) => console.log('Refined:', refined)}
 * />
 * ```
 */

import { FABButton } from '@refinevoice/core';
import Placeholder from '@tiptap/extension-placeholder';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { toast } from 'sonner';
import '@refinevoice/core/styles.css';
import { InterimText, WordWithAlternatives } from '@refinevoice/core';
import {
  useWidgetRefinementStore,
  useWidgetTranscriptStore,
  WidgetStoresProvider,
} from '../contexts/WidgetStoresContext';
import { useWidgetRecording } from '../hooks/useWidgetRecording';
import { refinementService } from '../services';
import { usePermissionStore } from '../store/usePermissionStore';
import { useStyleStore } from '../store/useStyleStore';

import { PermissionDialog } from './PermissionDialog';
import { RefineButton } from './RefineButton';
import { RefineVoiceEditor } from './RefineVoiceEditor';
import './RefineButton.css';

interface RefineVoiceWidgetProps {
  /** Unique identifier for this widget instance (required for multi-widget support) */
  widgetId: string;

  /** Callback fired when transcript changes */
  onTranscriptChange?: (transcript: string) => void;

  /** Callback when recording state changes */
  onRecordingStateChange?: (isRecording: boolean) => void;

  /** Custom class name for styling */
  className?: string;

  /** Whether to show word alternatives popover */
  showWordAlternatives?: boolean;

  /** Whether to show FAB recording button */
  showRecordingButton?: boolean;

  /** ARIA label for accessibility */
  ariaLabel?: string;

  /** Initial transcript content */
  initialContent?: string;

  // Refine configuration
  /** Whether to show the refine button */
  showRefineButton?: boolean;

  /** Position of the refine button */
  refinePosition?: 'bottom-left' | 'bottom-right' | 'top-right';

  /** Callback fired when refine action is performed */
  onRefine?: (originalText: string, refinedText: string) => void;

  /** Default refine style to use */
  defaultRefineStyle?: string;

  /** Custom text for the refine button */
  refineButtonText?: string;
}

interface RefineErrorToast {
  title: string;
  message: string;
  /** How long the toast stays on screen. */
  durationMs: number;
}

/**
 * Maps a failed refinement to user-facing toast content.
 *
 * Every failure gets a toast - a refinement that dies silently looks to the
 * user like the click never happened.
 */
function refineErrorToast(errorMessage: string): RefineErrorToast {
  if (errorMessage.includes('429') || errorMessage.includes('limit reached')) {
    return {
      title: 'Daily Limit Reached',
      message: "You've used all your free refinements for today. Sign in for more!",
      durationMs: 5000,
    };
  }

  if (errorMessage.includes('401') || errorMessage.includes('authentication')) {
    return {
      title: 'Service Unavailable',
      message: 'Refinement service is temporarily unavailable. Please try again later.',
      durationMs: 4000,
    };
  }

  return {
    title: 'Refinement Failed',
    message: "Couldn't refine your text right now. Your text is unchanged - please retry.",
    durationMs: 5000,
  };
}

/**
 * Imperative handle for external control of the widget
 */
export interface RefineVoiceWidgetHandle {
  /** Start voice recording */
  startRecording: () => void;
  /** Stop voice recording */
  stopRecording: () => void;
  /** Toggle recording state */
  toggleRecording: () => void;
  /** Trigger text refinement */
  refine: () => Promise<void>;
  /** Check if currently recording */
  isRecording: () => boolean;
  /** Check if currently refining */
  isRefining: () => boolean;
  /** Get current text content */
  getText: () => string;
  /** Set text content */
  setText: (text: string) => void;
  /** Focus the editor */
  focus: () => void;
}

/**
 * Component that provides EditorContext and uses the recording hook
 */
const RefineVoiceWidgetWithEditor = forwardRef<RefineVoiceWidgetHandle, RefineVoiceWidgetProps>(
  function RefineVoiceWidgetWithEditor(
    {
      widgetId,
      onTranscriptChange,
      onRecordingStateChange,
      className = '',
      showRecordingButton = true,
      ariaLabel,
      initialContent = '',
      showRefineButton: _showRefineButton = false,
      refinePosition: _refinePosition = 'bottom-right',
      onRefine: _onRefine,
      defaultRefineStyle: _defaultRefineStyle = 'professional',
      refineButtonText: _refineButtonText,
    },
    ref
  ) {
    const [showPermissionDialog, setShowPermissionDialog] = useState(false);
    const [hasContent, setHasContent] = useState(initialContent.length > 0);
    const editorRef = useRef<any>(null);

    // Widget-specific refinement store
    const { refinementState, setRefinementState, setRefinementError } = useWidgetRefinementStore();

    // Global style store
    const { getSelectedStyle } = useStyleStore();

    // Global permission store (shared across all widgets)
    const { hasGrantedPermission, grantPermission } = usePermissionStore();

    // Create TipTap editor instance FIRST
    const editor = useEditor({
      extensions: [
        StarterKit,
        Placeholder.configure({
          placeholder: 'Start speaking or typing...', // Will be updated dynamically
        }),
        InterimText,
        WordWithAlternatives,
      ],
      content: initialContent,
      editable: true,
      onUpdate: ({ editor: editorInstance }) => {
        const newContent = editorInstance.getText();
        setHasContent(newContent.length > 0);
        onTranscriptChange?.(newContent);
      },
      autofocus: 'start',
    });

    // Use widget-specific stores from context
    const { transcript, interimTranscript } = useWidgetTranscriptStore();

    // Now use the recording hook - pass editor directly
    const recording = useWidgetRecording(editor);

    // Update editor placeholder when recording state changes
    useEffect(() => {
      if (!editor) return;

      const placeholderExtension = editor.extensionManager.extensions.find(
        (ext) => ext.name === 'placeholder'
      );
      if (placeholderExtension) {
        placeholderExtension.options.placeholder = recording.isRecording
          ? 'Listening...'
          : ariaLabel || 'Start speaking or typing...';
      }
    }, [recording.isRecording, editor, ariaLabel]);

    // Update editor content when transcript changes externally
    // Only sync if transcript is non-empty and editor is empty (to prevent unwanted overwrites)
    useEffect(() => {
      if (recording.isRecording) return;

      const currentContent = editor.getText();
      // Only sync if we have content in transcript but editor is empty
      if (transcript.trim() && !currentContent.trim()) {
        const currentPos = editor.state.selection.from;
        editor.commands.setContent(transcript, { emitUpdate: false });
        editor.chain().focus().setTextSelection(currentPos).run();
      }
    }, [transcript, editor, recording.isRecording]);

    // Update editor editable state when recording changes
    // Allow editing even during recording - only disable during connection
    useEffect(() => {
      editor.setEditable(!recording.isConnecting);
    }, [recording.isConnecting, editor]);

    // Handle interim content updates
    useEffect(() => {
      if (interimTranscript?.trim()) {
        editor.commands.setInterim(interimTranscript);
      } else {
        editor.commands.clearInterim();
      }
    }, [interimTranscript, editor]);

    // Track the last processed transcript id to avoid duplicates
    const lastProcessedTranscriptIdRef = useRef<number>(0);

    // Handle final transcript insertion
    useEffect(() => {
      if (!recording.finalTranscript) return;

      // Skip if we've already processed this transcript (prevent duplicates)
      if (recording.finalTranscript.id <= lastProcessedTranscriptIdRef.current) {
        return;
      }
      lastProcessedTranscriptIdRef.current = recording.finalTranscript.id;

      // Clear interim text first
      editor.commands.clearInterim();

      // Handle word alternatives if available
      if (recording.finalTranscript.words && recording.finalTranscript.words.length > 0) {
        const wordsWithAlts = recording.finalTranscript.words.map((word, index) => {
          return {
            word: word.punctuated_word || word.word,
            confidence: word.confidence,
            alternatives: [], // TODO: Extract from alternatives if available
            wordIndex: index,
          };
        });

        // Set word data on the editor for the WordWithAlternatives extension
        editor.commands.setWordData(wordsWithAlts);
      }

      // Check if we need to replace a selection
      if (recording.finalTranscript.replaceSelection) {
        const { from, to } = recording.finalTranscript.replaceSelection;
        editor
          .chain()
          .deleteRange({ from, to })
          .insertContentAt(from, recording.finalTranscript.text + ' ')
          .run();
      } else {
        // Insert final text at current cursor position
        editor.commands.insertContent(recording.finalTranscript.text + ' ');
      }
    }, [recording.finalTranscript, editor]);

    // Notify parent of recording state changes
    useEffect(() => {
      onRecordingStateChange?.(recording.isRecording);
    }, [recording.isRecording, onRecordingStateChange]);

    // Handle record button click
    const handleRecordClick = useCallback(() => {
      if (!hasGrantedPermission) {
        setShowPermissionDialog(true);
      } else {
        // Don't delete selection here - let startRecording() capture it
        // The selection will be replaced when the final transcript arrives
        recording.startRecording();
      }
    }, [hasGrantedPermission, recording]);

    const handlePermissionAccept = () => {
      grantPermission();
      setShowPermissionDialog(false);
      recording.startRecording();
    };

    const handlePermissionDecline = () => {
      setShowPermissionDialog(false);
    };

    // Handle refinement
    const handleRefine = useCallback(async () => {
      if (!editor) return;

      const currentText = editor.getText();
      if (!currentText.trim()) {
        setRefinementError('No text to refine');
        return;
      }

      try {
        setRefinementState('refining');
        setRefinementError(null);

        const selectedStyle = getSelectedStyle();
        const result = await refinementService.refineWithStyle(
          currentText,
          selectedStyle.id as 'professional' | 'casual' | 'technical' | 'concise' | 'friendly'
        );

        // Update editor content with refined text
        editor.commands.setContent(result.refinedText, { emitUpdate: true });

        // Call optional callback
        _onRefine?.(currentText, result.refinedText);

        setRefinementState('success');
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Refinement failed';

        // Always surface the failure inline - the store error alone is invisible
        const { title, message, durationMs } = refineErrorToast(errorMessage);
        toast.error(title, { description: message, duration: durationMs });

        setRefinementError(errorMessage);
        setRefinementState('error');
      }
    }, [editor, getSelectedStyle, setRefinementState, setRefinementError, _onRefine]);

    // Handle save as prompt (placeholder - not implemented in widget context)
    const handleSaveAsPrompt = useCallback(() => {
      // This would typically open a dialog to save current style as custom prompt
      // For widget context, we might not support this feature
      console.log('Save as prompt not implemented in widget context');
    }, []);

    // Handle manage prompts (placeholder - not implemented in widget context)
    const handleManagePrompts = useCallback(() => {
      // This would typically open a dialog to manage custom prompts
      // For widget context, we might not support this feature
      console.log('Manage prompts not implemented in widget context');
    }, []);

    // Expose imperative API via ref
    useImperativeHandle(
      ref,
      () => ({
        startRecording: () => {
          if (!recording.isRecording) {
            handleRecordClick();
          }
        },
        stopRecording: () => {
          if (recording.isRecording) {
            recording.stopRecording();
          }
        },
        toggleRecording: () => {
          if (recording.isRecording) {
            recording.stopRecording();
          } else {
            handleRecordClick();
          }
        },
        refine: handleRefine,
        isRecording: () => recording.isRecording,
        isRefining: () => refinementState === 'refining',
        getText: () => editor?.getText() ?? '',
        setText: (text: string) => {
          editor?.commands.setContent(text, { emitUpdate: true });
        },
        focus: () => {
          editor?.commands.focus();
        },
      }),
      [recording, handleRecordClick, handleRefine, refinementState, editor]
    );

    const widgetClassName = `refinevoice-widget refinevoice-widget-${widgetId} ${className}`.trim();

    return (
      <>
        {/* biome-ignore lint/a11y/useSemanticElements: Widget container needs div for styling */}
        <div
          className={widgetClassName}
          role="region"
          aria-label={ariaLabel || `RefineVoice Widget ${widgetId}`}
        >
          <RefineVoiceEditor
            widgetId={widgetId}
            content={transcript}
            interimContent={interimTranscript}
            className="refinevoice-widget-editor"
            editorRef={editorRef}
            editor={editor}
          />

          {/* FAB Recording Button */}
          {showRecordingButton && (
            <FABButton
              icon={recording.isRecording ? '⏹️' : '🎤'}
              active={recording.isRecording}
              disabled={recording.isConnecting}
              onClick={() => {
                if (recording.isRecording) {
                  recording.stopRecording();
                } else {
                  handleRecordClick();
                }
              }}
              ariaLabel={recording.isRecording ? 'Stop recording' : 'Start recording'}
              position="bottom-right"
              className="refinevoice-fab-mic"
            />
          )}

          {/* Refine Button */}
          {_showRefineButton && (
            <div
              className={`refinevoice-refine-button refinevoice-refine-button-${_refinePosition}`}
              style={{
                position: 'absolute',
                bottom:
                  _refinePosition === 'bottom-left' || _refinePosition === 'bottom-right'
                    ? '84px'
                    : 'auto',
                top: _refinePosition === 'top-right' ? '20px' : 'auto',
                left: _refinePosition === 'bottom-left' ? '16px' : 'auto',
                right:
                  _refinePosition === 'bottom-right' || _refinePosition === 'top-right'
                    ? '16px'
                    : 'auto',
                zIndex: 10,
              }}
            >
              <RefineButton
                onRefine={handleRefine}
                disabled={refinementState === 'refining' || !hasContent}
                isRefining={refinementState === 'refining'}
                onSaveAsPrompt={handleSaveAsPrompt}
                onManagePrompts={handleManagePrompts}
              />
            </div>
          )}

          {/* Permission Dialog */}
          <PermissionDialog
            isOpen={showPermissionDialog}
            onAccept={handlePermissionAccept}
            onDecline={handlePermissionDecline}
          />

          <style>{`
          .refinevoice-widget {
            position: relative;
            height: 100%;
            flex-grow: 1;
          }
          .refinevoice-widget-editor {
            height: 100%;
            flex-grow: 1;
          }
          .refinevoice-refine-button {
            z-index: 10;
          }
          .refinevoice-refine-button-bottom-right {
            bottom: 84px;
            right: 16px;
          }
          .refinevoice-refine-button-bottom-left {
            bottom: 84px;
            left: 16px;
          }
          .refinevoice-refine-button-top-right {
            top: 20px;
            right: 16px;
          }
        `}</style>
        </div>
      </>
    );
  }
);

/**
 * RefineVoiceWidget - Public API
 *
 * Wraps the internal implementation with WidgetStoresProvider to ensure
 * each widget instance has isolated state.
 *
 * Supports ref for imperative control:
 * ```tsx
 * const widgetRef = useRef<RefineVoiceWidgetHandle>(null);
 *
 * // External control
 * widgetRef.current?.startRecording();
 * widgetRef.current?.refine();
 * ```
 */
export const RefineVoiceWidget = forwardRef<RefineVoiceWidgetHandle, RefineVoiceWidgetProps>(
  function RefineVoiceWidget(props, ref) {
    return (
      <WidgetStoresProvider widgetId={props.widgetId}>
        <RefineVoiceWidgetWithEditor {...props} ref={ref} />
      </WidgetStoresProvider>
    );
  }
);
